import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {convertIsoToLocal, convertIsoUTCToLocal} from "../../libs.jsx";
import { getSession } from "../../services/session";
// Replace with your public Mapbox token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

// Add custom popup styles
const popupStyles = `
    .mapboxgl-popup-close-button {
        color: white;
        font-size: 20px;
        padding: 4px;
    }
    
    .mapboxgl-popup-close-button:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    .mapboxgl-popup-content {
        background-color: #0081C7;
        color: white;
    }`;

// Add styles to document
const styleElement = document.createElement('style');
styleElement.textContent = popupStyles;
document.head.appendChild(styleElement);

const Map = forwardRef(({ locations, alertedDevices }, ref) => {
    let dData = getSession();

    const mapContainer = useRef(null);
    const map = useRef(null);
    const markers = useRef({});
    const [lng] = useState(-98.5795); // Center of contiguous US longitude
    const [lat] = useState(39.8283);  // Center of contiguous US latitude
    const [zoom] = useState(3.5);     // Adjusted zoom level to show entire contiguous US
    const [selectedMarker, setSelectedMarker] = useState(null);

    const mapContainerStyle = {
        width: '100%',
        height: '100%',
        position: 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
    };

    const clearSelectedMarker = () => {
        if (selectedMarker && markers.current[selectedMarker]) {
            const marker = markers.current[selectedMarker];
            const el = marker.getElement();
            el.style.filter = 'none';
            const popup = marker.getPopup();
            if (popup && popup.isOpen()) {
                popup.remove();
            }
        }
        setSelectedMarker(null);
    };

    useEffect(() => {
        if (map.current){
            return;
        }
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [lng, lat],
            zoom: zoom
        });
        return () => map.current?.remove();
    }, []);

    useEffect(() => {
        if (!map.current){
            return;
        }

        // Store the current selection state
        const wasSelected = selectedMarker;
        const wasOpen = wasSelected && markers.current[wasSelected]?.getPopup()?.isOpen();

        // Clear existing markers
        Object.values(markers.current).forEach(marker => {
            if (marker) {
                const popup = marker.getPopup();
                if (popup){
                    popup.remove();
                }
                marker.remove();
            }
        });
        markers.current = {};

        // Add new markers
        locations.forEach(location => {
            if (!map.current){
                return;
            }

            const isAlerted = alertedDevices.some(device => device.manufacture_uuid === location.manufacture_uuid);

            const marker = new mapboxgl.Marker({
                    color: isAlerted ? '#ff0000' : '#0081C7',
                    scale: isAlerted ? 1.2 : 1.0
                }).setLngLat([location.longitude, location.latitude]).setPopup(new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: false,
                    className: 'custom-popup'
                }).setHTML(`
                    <div class="p-3 w-auto">
                        <div>
                            <h3 class="text-white d-block mb-0 fs-4">Name:</h3>
                            <h3 class="text-white d-block mt-0">${location.resident_name}</h3>
                        </div>
                        <div>
                            <h3 class="text-white d-block mb-0 fs-4">Pendant #:</h3>
                            <h3 class="text-white d-block mt-0">${location.manufacture_uuid}</h3>
                        </div>
                        <div>
                            <h3 class="text-white d-block mb-0 fs-4">Last Ping:</h3>
                            <h3 class="text-white d-block mt-0">${convertIsoUTCToLocal(location.create_time, dData?.campusData[dData.acnt]?.tz_offset)}</h3>
                        </div>
                        ${isAlerted ? '<h3 class="text-danger">ALERTED DEVICE</h3>' : ''}
                    </div>
                `)).addTo(map.current);

            marker.getElement().addEventListener('click', () => {
                Object.values(markers.current).forEach(otherMarker => {
                    if (otherMarker && otherMarker !== marker) {
                        const popup = otherMarker.getPopup();
                        if (popup && popup.isOpen()) {
                            popup.remove();
                        }
                    }
                });
            });
            markers.current[location.manufacture_uuid] = marker;
        });

        // Restore selection state
        if (wasSelected && markers.current[wasSelected]) {
            const marker = markers.current[wasSelected];
            const el = marker.getElement();
            el.style.filter = 'brightness(1.2)';
            if (wasOpen) {
                const popup = marker.getPopup();
                if (popup) {
                    popup.addTo(map.current);
                }
            }
        }
    }, [locations, alertedDevices]);

    useImperativeHandle(ref, () => ({
        flyTo: (options) => {
            if (map.current) {
                map.current.flyTo({
                    ...options,
                    duration: 2000,
                    easing: (t) => t * (2 - t),
                    maxDuration: 3000,
                    essential: true
                });
            }
        },
        selectMarker: (manufacture_uuid) => {
            Object.values(markers.current).forEach(marker => {
                if (marker) {
                    const popup = marker.getPopup();
                    if (popup && popup.isOpen()) {
                        popup.remove();
                    }
                }
            });

            if (selectedMarker && markers.current[selectedMarker]) {
                const marker = markers.current[selectedMarker];
                const el = marker.getElement();
                el.style.filter = 'none';
            }

            setSelectedMarker(manufacture_uuid);

                const marker = markers.current[manufacture_uuid];
            if (marker && map.current) {
                const el = marker.getElement();
                el.style.filter = 'brightness(1.2)';

                const popup = marker.getPopup();
                if (popup) {
                    popup.addTo(map.current);
                }
            }
        }
    }));
    return <div ref={mapContainer} style={mapContainerStyle} />;
});

export default Map;